import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Trash2, Save, X, Check } from 'lucide-react';

// ==================== TYPES ====================

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive' | 'warning';
  isLoading?: boolean;
}

// ==================== COMPONENT ====================

/**
 * Reusable confirmation dialog with standardized styling
 * Reduces duplicate confirmation logic across components
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText = 'Hủy',
  variant = 'default',
  isLoading = false,
}) => {
  // ==================== VARIANT CONFIGURATIONS ====================
  
  const getVariantConfig = () => {
    switch (variant) {
      case 'destructive':
        return {
          icon: <Trash2 className="h-6 w-6 text-destructive" />,
          confirmText: confirmText || 'Xóa',
          confirmClass: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="h-6 w-6 text-orange-500" />,
          confirmText: confirmText || 'Tiếp tục',
          confirmClass: 'bg-orange-500 text-white hover:bg-orange-600',
        };
      default:
        return {
          icon: <Check className="h-6 w-6 text-primary" />,
          confirmText: confirmText || 'Xác nhận',
          confirmClass: 'bg-primary text-primary-foreground hover:bg-primary/90',
        };
    }
  };

  const config = getVariantConfig();

  // ==================== HANDLERS ====================
  
  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            {config.icon}
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-left">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel 
            onClick={handleCancel}
            disabled={isLoading}
          >
            <X className="h-4 w-4 mr-2" />
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={config.confirmClass}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Đang xử lý...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {variant === 'destructive' ? (
                  <Trash2 className="h-4 w-4" />
                ) : variant === 'warning' ? (
                  <AlertTriangle className="h-4 w-4" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {config.confirmText}
              </div>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ==================== COMMON CONFIRMATION PATTERNS ====================

/**
 * Hook for managing confirmation dialog state
 */
export const useConfirmDialog = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [config, setConfig] = React.useState<{
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive' | 'warning';
    confirmText?: string;
  } | null>(null);

  const openConfirmDialog = (dialogConfig: typeof config) => {
    setConfig(dialogConfig);
    setIsOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsOpen(false);
    setConfig(null);
  };

  const handleConfirm = () => {
    if (config) {
      config.onConfirm();
    }
    closeConfirmDialog();
  };

  return {
    isOpen,
    config,
    openConfirmDialog,
    closeConfirmDialog,
    handleConfirm,
  };
};

// ==================== PRESET CONFIRMATION DIALOGS ====================

export interface DeleteConfirmProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName: string;
  itemType?: string;
  isLoading?: boolean;
}

/**
 * Preset delete confirmation dialog
 */
export const DeleteConfirmDialog: React.FC<DeleteConfirmProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  itemType = 'item',
  isLoading = false,
}) => {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={`Xóa ${itemType}`}
      description={`Bạn có chắc chắn muốn xóa "${itemName}"? Hành động này không thể hoàn tác.`}
      variant="destructive"
      confirmText="Xóa"
      isLoading={isLoading}
    />
  );
};

export interface SaveConfirmProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  isLoading?: boolean;
}

/**
 * Preset save confirmation dialog
 */
export const SaveConfirmDialog: React.FC<SaveConfirmProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Lưu thay đổi',
  description = 'Bạn có chắc chắn muốn lưu những thay đổi này?',
  isLoading = false,
}) => {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={title}
      description={description}
      variant="default"
      confirmText="Lưu"
      isLoading={isLoading}
    />
  );
};